import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { MaterialModule } from '../../../../shared/material.module';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { DriverService, Driver } from '../../../../services/driver.service';
import { ClientConfigurationService } from '../../../../services/client-configuration.service';
import { InternationalPhoneInputComponent } from '../../../../shared/components/international-phone-input/international-phone-input.component';
import { VehicleUtils } from '../../../../utils/vehicle.utils';

@Component({
  selector: 'app-step-driver-vehicle-data',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    TranslatePipe,
    InternationalPhoneInputComponent,
  ],
  templateUrl: './step-driver-vehicle-data.component.html',
  styleUrl: './step-driver-vehicle-data.component.scss',
})
export class StepDriverVehicleDataComponent implements OnInit, OnDestroy {
  @Input() form!: FormGroup;
  @Input() preselectedTarga?: string;
  @Input() vehicleInfo?: any;
  @Input() crashesInfoData?: any;

  public availableDrivers: Driver[] = [];
  public isLoadingDrivers = false;
  public isPhoneValid = false;
  public VehicleUtils = VehicleUtils;

  public driverSearchTerm = '';

  private destroy$ = new Subject<void>();

  constructor(
    private driverService: DriverService,
    private clientConfigurationService: ClientConfigurationService,
    private cdr: ChangeDetectorRef
  ) {}

  get showVehicleFields(): boolean {
    return this.vehicleInfo?.model != null || this.vehicleInfo?.manufacturer != null;
  }

  get areVehicleFieldsReadonly(): boolean {
    return this.showVehicleFields;
  }

  get hasAssociatedDriver(): boolean {
    return this.crashesInfoData?.driverInfo != null;
  }

  getDriverDisplayName(driver: Driver): string {
    return this.clientConfigurationService.getDriverSelectLabel(driver);
  }

  get filteredDrivers(): Driver[] {
    const term = this.normalize(this.driverSearchTerm);
    if (!term) return this.availableDrivers;

    return this.availableDrivers.filter((d) => {
      const label = this.normalize(this.clientConfigurationService.getDriverSelectLabel(d));
      const code = this.normalize(d.driverCardNumber ?? '');
      const name = this.normalize(d.name ?? '');
      const surname = this.normalize(d.surname ?? '');
      const surnameFirst = `${surname} ${name}`.trim();
      const nameFirst = `${name} ${surname}`.trim();
      return (
        label.includes(term) ||
        code.includes(term) ||
        name.includes(term) ||
        surname.includes(term) ||
        surnameFirst.includes(term) ||
        nameFirst.includes(term)
      );
    });
  }

  onDriverSearchChange(value: string): void {
    this.driverSearchTerm = value ?? '';
  }

  onDriverSelectOpenedChange(opened: boolean): void {
    if (!opened) {
      this.driverSearchTerm = '';
    }
  }

  private normalize(value: string | null | undefined): string {
    return (value ?? '').toString().toLowerCase().trim();
  }

  private sortDriversList(drivers: Driver[]): Driver[] {
    return [...drivers].sort((a: Driver, b: Driver) =>
      this.clientConfigurationService.driverDisplayMode === 'DRIVER_CODE'
        ? (a.driverCardNumber || '').localeCompare(b.driverCardNumber || '', undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        : (a.surname || '').localeCompare(b.surname || '', undefined, { sensitivity: 'base' })
    );
  }

  ngOnInit() {
    this.clientConfigurationService.configurationChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.availableDrivers.length > 0) {
          this.availableDrivers = this.sortDriversList(this.availableDrivers);
        }
        this.cdr.detectChanges();
      });

    this.form.get('selectedDriverId')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((driverId) => this.applyPhoneFromDriver(driverId));

    this.loadDrivers();
    // Non preselezionare più "AUTISTA NON DISPONIBILE": serve un autista reale per procedere.
    // if (!this.hasAssociatedDriver) {
    //   this.form.patchValue({ selectedDriverId: VehicleUtils.NOT_AVAILABLE });
    // }
    const telefonoValue = this.form.get('telefono')?.value;
    this.isPhoneValid = telefonoValue && telefonoValue.trim() !== '';
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onPhoneValidationChange(isValid: boolean): void {
    this.isPhoneValid = isValid;
    this.cdr.detectChanges();
  }

  /** Compila il campo telefono con phoneNumber dell'autista selezionato, se presente. */
  private applyPhoneFromDriver(driverId: string | number | null): void {
    if (driverId == null || driverId === VehicleUtils.NOT_AVAILABLE) {
      this.form.patchValue({ telefono: '' }, { emitEvent: false });
      this.isPhoneValid = false;
      this.cdr.detectChanges();
      return;
    }
    const driver = this.availableDrivers.find(d => d.id === Number(driverId));
    const phone = driver?.phoneNumber ?? '';
    const value = (typeof phone === 'string' && phone.trim()) ? phone.trim() : '';
    this.form.patchValue({ telefono: value }, { emitEvent: false });
    this.isPhoneValid = !!value;
    this.cdr.detectChanges();
  }

  private loadDrivers(): void {
    this.isLoadingDrivers = true;
    const clientId = this.vehicleInfo?.clientId;
    if (!clientId) {
      this.availableDrivers = [];
      this.isLoadingDrivers = false;
      return;
    }

    this.driverService.getDrivers(clientId).subscribe({
      next: (response) => {
        this.availableDrivers = this.sortDriversList(response || []);

        if (this.hasAssociatedDriver && this.crashesInfoData?.driverInfo) {
          const associatedDriverId = this.crashesInfoData.driverInfo.driverId;
          if (associatedDriverId != null && associatedDriverId !== '') {
            // Imposta sempre l'id dal sinistro, anche se non compare nell'elenco (niente più fallback NOT_AVAILABLE).
            this.form.patchValue({ selectedDriverId: associatedDriverId });
          }
        }

        this.applyPhoneFromDriver(this.form.get('selectedDriverId')?.value);
        this.isLoadingDrivers = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.availableDrivers = [];
        this.isLoadingDrivers = false;
        this.cdr.detectChanges();
      }
    });
  }
}
